import Vue from 'vue'
import Router from 'vue-router'
import Dashboard from './views/Dashboard.vue'
import auth from './auth'
import store from './store'

Vue.use(Router)

export const dashboardRouteGuard = (to: any, from: any, next: any) => {
  store
    .dispatch('fetchAccountList')
    .then(() => {
      const accountId: string = to.params.accountId
      const accountList: string[] = store.state.accountList

      const currentAccount = accountList.find(account => {
        return account.toLowerCase() == accountId.toLowerCase()
      })
      if (currentAccount) {
        store
          .dispatch('setAccountId', currentAccount)
          .then(() => next())
          .catch(() => next('/choose-account'))
      } else {
        next('/choose-account')
      }
    })
    .catch(err => {
      console.error('Unable to load account list')
      next(new Error(err))
    })
}

const devRoutes =
  process.env.NODE_ENV === 'development'
    ? [
        {
          path: '/assets',
          name: 'assets',
          component: () => import('./views/Assets.vue'),
        },
        {
          path: '/data-services',
          name: 'data-services',
          component: () =>
            import(/* webpackChunkName: "data-services" */ './views/DataServices.vue'),
        },
      ]
    : []

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  routes: [
    {
      path: '/',
      redirect: '/choose-account',
    },
    {
      path: '/choose-account',
      name: 'choose-account',
      component: () => import('./views/ChooseAccount.vue'),
    },
    ...devRoutes,

    // Putting the account last or it'll eat up the others like assets, auth, etc
    {
      path: '/:accountId',
      component: Dashboard,
      beforeEnter: dashboardRouteGuard,
      children: [
        {
          path: '',
          redirect: 'inventory',
          component: () => import(/* webpackChunkName: "inventory" */ './views/Inventory.vue'),
        },
        {
          path: 'inventory/:partId',
          name: 'inventory-detail',
          props: true,
          component: () =>
            import(/* webpackChunkName: "inventory-detail" */ './views/InventoryDetail.vue'),
        },
        {
          path: 'inventory',
          name: 'inventory',
          component: () => import(/* webpackChunkName: "inventory" */ './views/Inventory.vue'),
        },
        {
          path: 'pulls',
          name: 'pulls',
          component: () => import(/* webpackChunkName: "pulls" */ './views/Pulls.vue'),
        },
        {
          path: 'pulls/:pullId',
          name: 'pull-detail',
          props: true,
          component: () => import(/* webpackChunkName: "pull-detail" */ './views/PullDetail.vue'),
        },
        {
          path: 'shipments',
          name: 'shipments',
          component: () => import(/* webpackChunkName: "shipments" */ './views/Shipments.vue'),
        },
        {
          path: 'shipments/:shipmentId',
          name: 'shipment-detail',
          props: true,
          component: () =>
            import(/* webpackChunkName: "shipment-detail" */ './views/ShipmentDetail.vue'),
        },
        {
          path: 'replenishments',
          name: 'replenishments',
          props: route => ({ id: route.query.id }),
          component: () =>
            import(/* webpackChunkName: "replenishments" */ './views/Replenishments.vue'),
        },
        {
          path: 'pull-inventory/:orderId?',
          name: 'pull-inventory',
          props: route => ({ orderId: route.params.orderId, partId: route.query.partId }),
          component: () =>
            import(/* webpackChunkName: "pull-inventory" */ './views/PullInventory.vue'),
        },
      ],
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

router.onError(err => {
  console.error(err)
  router.push('/choose-account')
})

router.beforeEach((to: any, from: any, next: any) => {
  if (to.matched.some((record: any) => record.meta.requiresAuthentication)) {
    // this route requires auth, check if logged in
    if (auth.isAuthenticated()) {
      // only proceed if authenticated.
      let checkRoles: any[] | never[] = []
      if (
        to.matched.some((record: any) => {
          if (record.meta.requireRoles) {
            checkRoles = checkRoles.concat(record.meta.requireRoles)
            return true
          }
          return false
        })
      ) {
        if (auth.checkRoles(checkRoles)) {
          // authorized to see the page
          next()
        } else {
          // not authorized to see page
          console.log('Not Authorized')
          next(from.fullPath)
        }
      } else next()
    } else {
      auth.signIn()
    }
  } else {
    next()
  }
})

export default router
